import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Users, Download, Play, Globe, Check, Shirt, MapPin, User, Wand2, Wrench, Camera } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { FiPrWordmark } from '../components/FiPrLogo';
import BetaApplyModal from '../components/BetaApplyModal';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Avatar stack for social-proof under the hero CTA
const AVATARS = [
  { initials: 'JM', bg: '#3B6E52' },
  { initials: 'SR', bg: '#3B4A8A' },
  { initials: 'TK', bg: '#7A2C2C' },
  { initials: 'AL', bg: '#4A5740' },
];

// Pricing tiers — non-Enterprise cards open the BetaApplyModal,
// Enterprise opens a mailto. Plan key is kept for future plan-aware
// onboarding without further churn.
const PRICING_TIERS = [
  {
    plan: 'free',
    name: 'Free / Student',
    price: '₹0',
    priceUsd: '($0)',
    cadence: '',
    bestFor: 'Students & Learning',
    features: [
      '1 project',
      '5 collaborators',
      '30-page script limit',
      'Basic AI breakdown',
      'Basic exports',
    ],
    popular: false,
    ctaLabel: 'Get Started',
  },
  {
    plan: 'indie',
    name: 'Indie',
    price: '₹1,499',
    priceUsd: '($20/mo)',
    cadence: '/mo',
    bestFor: 'Short Films & Indie Filmmakers',
    features: [
      '2 active projects',
      '10 collaborators',
      'Unlimited script pages',
      'Standard AI breakdowns',
      'Excel/PDF exports',
    ],
    popular: false,
    ctaLabel: 'Get Started',
  },
  {
    plan: 'standard',
    name: 'Standard',
    price: '₹4,999',
    priceUsd: '($59/mo)',
    cadence: '/mo',
    bestFor: 'Feature Films & Small Production Companies',
    features: [
      '4 active projects',
      '50 collaborators',
      'Advanced AI breakdowns',
      'All exports',
      'Revision history',
      'Priority support',
    ],
    popular: true,
    ctaLabel: 'Get Started',
  },
  {
    plan: 'studio',
    name: 'Studio',
    price: '₹16,999',
    priceUsd: '($199/mo)',
    cadence: '/mo',
    bestFor: 'Production Houses',
    features: [
      'Unlimited active projects',
      'Unlimited collaborators (Fair Use)',
      'API access',
      'Priority AI processing',
      'Premium support',
    ],
    popular: false,
    ctaLabel: 'Get Started',
  },
  {
    plan: 'enterprise',
    name: 'Enterprise',
    price: 'Custom',
    cadence: '',
    bestFor: 'Studios, Networks & OTT Platforms',
    features: [
      'Everything in Studio',
      'White-label',
      'SSO/SAML',
      'Custom integrations',
      'Dedicated account manager',
      'SLA',
      'Private deployment (optional)',
      'Custom AI limits',
    ],
    popular: false,
    ctaLabel: 'Contact Sales',
  },
];

const LANGUAGES = [
  { name: 'Malayalam', native: 'മലയാളം', industry: 'Mollywood' },
  { name: 'Tamil', native: 'தமிழ்', industry: 'Kollywood' },
  { name: 'Kannada', native: 'ಕನ್ನಡ', industry: 'Sandalwood' },
  { name: 'Hindi', native: 'हिन्दी', industry: 'Bollywood' },
  { name: 'Telugu', native: 'తెలుగు', industry: 'Tollywood' },
  { name: 'Marathi', native: 'मराठी', industry: 'Marathi Cinema' },
  { name: 'Bengali', native: 'বাংলা', industry: 'Tollygunge' },
  { name: 'English', native: 'English', industry: 'Worldwide' },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [showBeta, setShowBeta] = useState(false);
  const [betaCount, setBetaCount] = useState(120);

  useEffect(() => {
    axios.get(`${API}/beta/count`)
      .then(({ data }) => {
        if (typeof data?.count === 'number') setBetaCount(data.count);
      })
      .catch(() => { /* harmless — fall back to default */ });
  }, []);

  const handleTierCta = (tier) => {
    if (tier.plan === 'enterprise') {
      window.location.href =
        'mailto:sales@fipr.ai?subject=FiPr.ai%20Enterprise%20Inquiry';
      return;
    }
    setShowBeta(true);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]" style={{ color: '#f0f0f0' }}>
      {showBeta && <BetaApplyModal onClose={() => setShowBeta(false)} />}

      {/* ── Top Navbar ── */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 sm:px-10 h-[72px]"
        style={{
          background: 'rgba(10,10,10,0.92)',
          backdropFilter: 'blur(16px)',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
        }}
      >
        <FiPrWordmark iconSize={34} textSize="xl" />

        <div className="flex items-center gap-3">
          <Button
            onClick={() => navigate('/login')}
            variant="outline"
            className="border-white/20 hover:bg-white/8 hover:border-white/40 text-white font-semibold text-sm px-5 h-10 rounded-xl"
            data-testid="navbar-signin-btn"
          >
            Sign In
          </Button>
          <Button
            onClick={() => navigate('/register')}
            className="bg-[#F5A623] hover:bg-[#E0961B] text-black font-bold text-sm px-5 h-10 rounded-xl shadow-md shadow-[#F5A623]/25"
            data-testid="navbar-signup-btn"
          >
            Sign Up
          </Button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <div
        className="relative min-h-screen flex flex-col items-center justify-center px-6 pt-[72px]"
        style={{
          backgroundImage:
            'url(https://static.prod-images.emergentagent.com/jobs/ce0d3aaa-66b3-4e94-af93-bdc0d8933d34/images/16c3ed16ea59e16c30420208a823c1778465f0d45cfd86b5510e2d632be296eb.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/75 via-[#0a0a0a]/55 to-[#0a0a0a]" />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          {/* Pill */}
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
            style={{
              background: 'rgba(245,166,35,0.1)',
              border: '1px solid rgba(245,166,35,0.25)',
            }}
            data-testid="hero-pill"
          >
            <span
              className="h-2 w-2 rounded-full animate-pulse"
              style={{ background: '#F5A623' }}
              aria-hidden="true"
            />
            <p className="text-sm" style={{ color: 'rgba(245,166,35,0.95)' }}>
              Breakdowns done before your coffee cools.
            </p>
          </div>

          {/* Headline */}
          <h1 className="font-bold leading-tight mb-6" data-testid="hero-heading">
            <span className="block text-4xl sm:text-5xl lg:text-6xl text-white">
              Your Script.
            </span>
            <span className="block text-4xl sm:text-5xl lg:text-6xl text-[#F5A623]">
              Fully Broken Down.
            </span>
            <span className="block text-4xl sm:text-5xl lg:text-6xl text-white">
              In Minutes, Not Weeks.
            </span>
          </h1>

          {/* Subtext (single muted line) */}
          <p
            className="text-[#A0A4B8] text-base sm:text-lg mb-6 max-w-2xl mx-auto leading-relaxed"
            data-testid="hero-subtext"
          >
            Upload your screenplay and get industry-standard breakdowns
            organized by departments — instantly.
          </p>

          {/* Department chips */}
          <div
            className="flex flex-wrap gap-2 justify-center mt-6 mb-8"
            data-testid="hero-departments-chip-row"
          >
            {[
              { label: 'Wardrobe',  Icon: Shirt },
              { label: 'Locations', Icon: MapPin },
              { label: 'Cast',      Icon: User },
              { label: 'VFX',       Icon: Wand2 },
              { label: 'Props',     Icon: Wrench },
              { label: 'Camera',    Icon: Camera },
            ].map(({ label, Icon }) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm text-white"
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.18)',
                }}
                data-testid={`hero-chip-${label.toLowerCase()}`}
              >
                <Icon className="h-3.5 w-3.5" style={{ color: '#F5A623' }} strokeWidth={2} />
                {label}
              </span>
            ))}
            <span
              className="inline-flex items-center px-3 py-1.5 rounded-full text-xs sm:text-sm"
              style={{
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.08)',
                color: '#6B7280',
              }}
              data-testid="hero-chip-more"
            >
              +more
            </span>
          </div>

          {/* CTA + microcopy + social-proof avatar stack */}
          <div className="flex flex-col items-center gap-3">
            <Button
              onClick={() => navigate('/register')}
              className="bg-[#F5A623] hover:bg-[#E0961B] text-black font-semibold px-8 py-3 text-base h-auto rounded-lg shadow-lg shadow-[#F5A623]/25 transition-all duration-200"
              data-testid="hero-get-started-btn"
            >
              Start your free breakdown
            </Button>
            <p className="text-[#888] text-xs sm:text-sm" data-testid="hero-free-microcopy">
              Free during beta · No credit card required
            </p>

            {/* Social proof — avatar stack + live count */}
            <div className="flex items-center gap-3 mt-3" data-testid="hero-social-proof">
              <div className="flex -space-x-2">
                {AVATARS.map((a, i) => (
                  <div
                    key={i}
                    className="h-9 w-9 rounded-full flex items-center justify-center text-[11px] font-bold text-white ring-2 ring-[#0a0a0a]"
                    style={{ background: a.bg, zIndex: AVATARS.length - i }}
                  >
                    {a.initials}
                  </div>
                ))}
              </div>
              <p className="text-sm text-[#888]">
                <span className="text-[#F5A623] font-semibold" data-testid="hero-beta-count">
                  {betaCount}+ producers
                </span>{' '}
                already in beta
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── How It Works ── */}
      <div className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-semibold text-3xl sm:text-4xl text-center mb-16">
            How It Works
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card
              className="bg-[#13151C] border-[#1E2028] p-6 rounded-lg hover:-translate-y-1 transition-all duration-200"
              data-testid="feature-upload"
            >
              <div className="h-12 w-12 rounded-full bg-[#F5A623]/10 flex items-center justify-center mb-4">
                <Play className="h-6 w-6 text-[#F5A623]" strokeWidth={2} />
              </div>
              <h3 className="font-semibold text-xl mb-2">Upload Script</h3>
              <p className="text-[#A0A4B8] text-sm">
                PDF, Final Draft, Fountain, or text format supported—in
                Malayalam, Tamil, Kannada, Hindi, Telugu, Marathi, or Bengali.
              </p>
            </Card>

            <Card
              className="bg-[#13151C] border-[#1E2028] p-6 rounded-lg hover:-translate-y-1 transition-all duration-200"
              data-testid="feature-ai"
            >
              <div className="h-12 w-12 rounded-full bg-[#4a9eff]/10 flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-[#4a9eff]" strokeWidth={2} />
              </div>
              <h3 className="font-semibold text-xl mb-2">AI Breakdown</h3>
              <p className="text-[#A0A4B8] text-sm">
                FiPr AI extracts scenes, props, costumes—across 7 languages
                with dual-language breakdowns preserving cultural details.
              </p>
            </Card>

            <Card
              className="bg-[#13151C] border-[#1E2028] p-6 rounded-lg hover:-translate-y-1 transition-all duration-200"
              data-testid="feature-departments"
            >
              <div className="h-12 w-12 rounded-full bg-[#F5A623]/10 flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-[#F5A623]" strokeWidth={2} />
              </div>
              <h3 className="font-semibold text-xl mb-2">
                Department Dashboards
              </h3>
              <p className="text-[#A0A4B8] text-sm">
                Production Design, Costume, AD, Location, Props—each crew
                member sees only their relevant data in their preferred
                language.
              </p>
            </Card>

            <Card
              className="bg-[#13151C] border-[#1E2028] p-6 rounded-lg hover:-translate-y-1 transition-all duration-200"
              data-testid="feature-export"
            >
              <div className="h-12 w-12 rounded-full bg-[#4a9eff]/10 flex items-center justify-center mb-4">
                <Download className="h-6 w-6 text-[#4a9eff]" strokeWidth={2} />
              </div>
              <h3 className="font-semibold text-xl mb-2">Export & Share</h3>
              <p className="text-[#A0A4B8] text-sm">
                Excel, PDF, or shareable links—in original language or English
                for seamless team collaboration.
              </p>
            </Card>
          </div>
        </div>
      </div>

      {/* ── Built for Indian Cinema (NEW — deep dark, gold accents) ── */}
      <section
        className="py-20 lg:py-28 px-4 border-y border-[#1E2028]"
        style={{ background: '#000' }}
        data-testid="built-for-indian-cinema-section"
      >
        <div className="max-w-7xl mx-auto">
          {/* Top-left gold pill */}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F5A623]/10 border border-[#F5A623]/30 text-[#F5A623] text-xs font-semibold mb-10 tracking-wider">
            <Globe className="h-3.5 w-3.5" strokeWidth={2} />
            NEW · INDIAN CINEMA READY
          </div>

          {/* Two-column grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 items-start">
            {/* Left column — heading + paragraph + checklist + closing line + CTA */}
            <div>
              <h2 className="font-semibold text-3xl sm:text-4xl lg:text-5xl mb-6 leading-tight">
                Built for{' '}
                <span className="text-[#F5A623]">Indian-Language Cinema.</span>
              </h2>
              <p className="text-base sm:text-lg text-[#A0A4B8] leading-relaxed mb-8">
                Most production software struggles with Indian-language
                scripts, leading to broken text, inaccurate parsing, and
                unusable reports. FiPr.ai is built specifically for Indian
                filmmaking.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Supports Malayalam, Tamil, Kannada, Hindi, Telugu, Marathi, and Bengali',
                  'Native script parsing — no transliteration required',
                  'Breakdowns in both the original language and English',
                  'Automatically detects INT/EXT, DAY/NIGHT, and scene details',
                  'Understands culturally specific props, costumes, vehicles, and settings',
                  'Export reports without garbled characters or formatting issues',
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-[#A0A4B8] text-sm sm:text-base"
                  >
                    <Check
                      className="h-5 w-5 text-[#F5A623] flex-shrink-0 mt-0.5"
                      strokeWidth={2.5}
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm sm:text-base text-[#888] italic">
                From screenplay to production-ready breakdown in minutes.
              </p>
            </div>

            {/* Right column — 8 language badges in 3-col grid */}
            <div
              className="grid grid-cols-2 sm:grid-cols-3 gap-3"
              data-testid="language-badges"
            >
              {LANGUAGES.map((lang) => (
                <div
                  key={lang.name}
                  className="bg-[#0a0a0a] border border-[#1E2028] hover:border-[#F5A623]/60 rounded-lg p-4 transition-all duration-200 hover:-translate-y-0.5 text-left"
                  data-testid={`lang-badge-${lang.name.toLowerCase()}`}
                >
                  <div className="font-semibold text-2xl text-white mb-1 leading-tight">
                    {lang.native}
                  </div>
                  <div className="text-sm text-white font-medium">
                    {lang.name}
                  </div>
                  <div className="text-xs text-[#A0A4B8] mt-0.5">
                    {lang.industry}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing (5 tiers) ── */}
      <div className="py-24 px-4 bg-[#0a0a0a]" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-semibold text-3xl sm:text-4xl text-center mb-4">
            Simple Pricing
          </h2>
          <p className="text-[#A0A4B8] text-center mb-16 max-w-2xl mx-auto">
            From short films to studios — pick the tier that fits your slate.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {PRICING_TIERS.map((tier) => (
              <Card
                key={tier.plan}
                className={`relative p-6 rounded-lg flex flex-col bg-[#13151C] transition-all duration-200 ${
                  tier.popular
                    ? 'border-[#F5A623] shadow-lg shadow-[#F5A623]/15 lg:-translate-y-2'
                    : 'border-[#1E2028] hover:border-[#2A2D38]'
                }`}
                data-testid={`pricing-card-${tier.plan}`}
              >
                {tier.popular && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#F5A623] text-black px-4 py-1 rounded-full text-xs font-semibold"
                    data-testid="pricing-popular-badge"
                  >
                    POPULAR
                  </div>
                )}

                <h3 className="font-semibold text-2xl mb-1">{tier.name}</h3>
                <p className="text-xs text-[#A0A4B8] uppercase tracking-wider mb-4 min-h-[2.5rem]">
                  {tier.bestFor}
                </p>

                <div className="mb-5">
                  <div>
                    <span className="text-3xl font-bold text-[#F5A623]">
                      {tier.price}
                    </span>
                    {tier.cadence && (
                      <span className="text-sm text-[#A0A4B8] ml-1">
                        {tier.cadence}
                      </span>
                    )}
                  </div>
                  {tier.priceUsd && (
                    <div className="text-xs text-[#6B7280] mt-1" data-testid={`pricing-usd-${tier.plan}`}>
                      {tier.priceUsd}
                    </div>
                  )}
                </div>

                <ul className="space-y-2 text-sm text-[#A0A4B8] mb-6 flex-grow">
                  {tier.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-[#F5A623] flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleTierCta(tier)}
                  className={
                    tier.popular
                      ? 'w-full bg-[#F5A623] hover:bg-[#E0961B] text-black font-semibold rounded-lg'
                      : 'w-full bg-white/5 hover:bg-white/10 text-white border border-[#1E2028] hover:border-[#F5A623]/40 font-semibold rounded-lg'
                  }
                  data-testid={`pricing-cta-${tier.plan}`}
                >
                  {tier.ctaLabel}
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* ── Final CTA ── */}
      <div className="py-24 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-semibold text-3xl sm:text-4xl mb-6">
            Ready to break down your script?
          </h2>
          <p className="text-lg text-[#A0A4B8] mb-8">
            Join{' '}
            <span className="text-[#F5A623] font-semibold">
              {betaCount}+ producers
            </span>{' '}
            already using FiPr.ai.
          </p>
          <Button
            onClick={() => navigate('/register')}
            className="bg-[#F5A623] hover:bg-[#E0961B] text-black font-semibold px-8 py-6 text-lg rounded-lg transition-all duration-200"
            data-testid="final-cta-btn"
          >
            Get Started
          </Button>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer className="border-t border-[#1E2028] py-8 px-4">
        <div className="max-w-6xl mx-auto text-center text-[#A0A4B8] text-sm">
          <p>© 2026 FiPr.ai. AI-powered film production breakdown.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
